import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useTranslation } from 'react-i18next';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';

import {
  MainMenuUserItem,
  MainMenuCommunityItem,
  PlusButton,
  FaqButton,
  Tooltip,
} from 'components';
import { openModal, closeModal } from 'store/actionCreators/modal';
import { getAuthUser, getNotifications } from 'store/selectors/auth';
import { selectCurrentUserSubscription } from 'store/selectors/subscriptions';
import { getIsMenuOpen } from 'store/selectors/currentCommunity';
import { getCommunities } from 'store/selectors/communities';
import { doUpdateCommunitiesOrder } from 'store/actionCreators/communities';
import { checkUnreadNotifications } from 'utils/common';
import { CREATE_COMMUNITY } from 'constants/sidebars';
import { INFO_POPUP_V2 } from 'constants/popups';
import { CommunityRoles, CommunityListItem } from 'types';
import { Grid3x3Icon, Logo } from 'static';

import {
  StyledMainMenuBlock,
  StyledMainMenuTopBlock,
  StyledSeparator,
  StyledFaqButtonBlock,
  StyledMainMenuCommunities,
  StyledBottomMenu,
  StyledGrid3x3ButtonBlock,
} from './styled';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import { useNavigate } from 'react-router-dom';

// Helper to reorder array
function reorder(
  list: CommunityListItem[],
  startIndex: number,
  endIndex: number,
): CommunityListItem[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

const MainMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const communities = useSelector(getCommunities);
  const isMenuOpen = useSelector(getIsMenuOpen);
  const notifications = useSelector(getNotifications);
  const subscription = useSelector(selectCurrentUserSubscription);
  const userProfile = useSelector(getAuthUser);
  const tooltipProps = {
    tooltipStyle: {
      fontSize: 12,
      maxWidth: 200,
      whiteSpace: 'pre-wrap',
      lineBreak: 'normal',
    },
  };
  const handleRedirect = () => {
    dispatch(closeModal());
    navigate(`${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`, {
      replace: true,
    });
  };

  const onAddCommunity = () => {
    if ((subscription?.info?.communities || 0) < subscription.maxCommunities) {
      dispatch(openModal({ type: CREATE_COMMUNITY, placement: 'right' }));
    } else {
      dispatch(
        openModal({
          type: INFO_POPUP_V2,
          data: {
            headerText: t('community.createOverLimitTitle'),
            messageText: t('community.createOverLimitText'),
            defaultButtonText: 'Ok',
            type: 'warning',
            withoutCloseButton: true,
            onConfirm: () => handleRedirect(),
          },
        }),
      );
    }
  };

  // Drag-and-drop for communities
  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const newOrder = reorder(
      communities,
      result.source.index,
      result.destination.index,
    );

    // Save to localStorage for persistence
    localStorage.setItem(
      'communityOrder',
      JSON.stringify(newOrder.map((c) => c.id)),
    );

    // Get the moved community and its new position
    const movedCommunity = communities[result.source.index];
    const newPosition = result.destination.index;

    // Dispatch to Redux to update the order immediately and call API
    dispatch(
      doUpdateCommunitiesOrder.trigger({
        communities: newOrder,
        communityId: movedCommunity.id,
        end: newPosition,
      }),
    );
  };

  if (!userProfile) {
    return null;
  }

  return (
    <StyledMainMenuBlock isMenuOpen={isMenuOpen}>
      <StyledMainMenuTopBlock>
        <MainMenuUserItem
          image={userProfile.smallLogo || userProfile.logo}
          hasNotifications={checkUnreadNotifications(notifications)}
        />
        <PerfectScrollbar
          style={{
            width: '100%',
            maxHeight: 'calc(100svh - 324px)',
            marginBottom: '8px',
          }}
          className={'communities-list-scrollbar'}
          options={{
            wheelSpeed: 0.2,
            wheelPropagation: false,
            minScrollbarLength: 4,
          }}
        >
          <StyledMainMenuCommunities>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="communities-list">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {communities &&
                      communities.map((community, index) => (
                        <Draggable
                          key={community.id}
                          draggableId={community.id}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <MainMenuCommunityItem
                                index={index}
                                isShowTooltip
                                title={community.name}
                                image={community.logo}
                                id={community.id}
                                isDisabled={
                                  community.users &&
                                  community.users[0]?.communityPermission
                                    ?.role === CommunityRoles.BLOCKED
                                }
                                invitesCount={
                                  communities.find((c) => c.id === community.id)
                                    ?.requests?.length || 0
                                }
                                userRole={
                                  community.users &&
                                  community.users[0]?.communityPermission?.role
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </StyledMainMenuCommunities>
        </PerfectScrollbar>
        <PlusButton
          contained
          tooltipStyle={tooltipProps.tooltipStyle}
          text={t('common.toolTipText.communityPlusIcon')}
          position="right-center"
          isVisible
          onClick={onAddCommunity}
        />
        <StyledBottomMenu>
          <Tooltip
            {...tooltipProps}
            isVisible
            text={t('common.toolTipText.grid3x3')}
            position="right-center"
          >
            <StyledGrid3x3ButtonBlock
              onClick={() =>
                navigate(
                  `${ROUTES.PROFILE}/${PROFILE_ROUTES.COMMUNITIES}#matrix`,
                )
              }
            >
              <Grid3x3Icon color="#304659" width={28} height={28} />
            </StyledGrid3x3ButtonBlock>
          </Tooltip>
        </StyledBottomMenu>
        <StyledSeparator />
        <Tooltip
          {...tooltipProps}
          isVisible
          text={t('common.toolTipText.logoGumb')}
          position="right-center"
        >
          <img src={Logo} alt="" />
        </Tooltip>
      </StyledMainMenuTopBlock>
      <StyledBottomMenu>
        <Tooltip
          {...tooltipProps}
          isVisible
          text={t('common.toolTipText.faq')}
          position="right-center"
        >
          <StyledFaqButtonBlock>
            <FaqButton />
          </StyledFaqButtonBlock>
        </Tooltip>
      </StyledBottomMenu>
    </StyledMainMenuBlock>
  );
};

MainMenu.defaultProps = {
  communities: [],
  userProfile: {},
  notifications: [],
};

export default MainMenu;
